import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PdfCard from 'app/shared/components/document/DocumentView/PdfCard';
import _, { keyBy } from 'lodash';
import PropTypes from 'prop-types';

import {
  FormBuilderMethodType,
  FormBuilderOperationType,
  ReactRenderingContent,
} from 'constants/formBuilder';
import { FormType } from 'constants/forms';
import { checkIsTagAssignedDate, checkIsTagName } from 'utils/forms';

import {
  deleteTemplateElementAction,
  updateTemplateElementAction,
  updateTemplateElementMultipleOptionsAction,
} from 'store/forms/actions';
import {
  selectDynamicFieldsMap,
  selectDynamicFieldsNamesById,
  selectDynamicFieldsSchema,
  selectPdfFormPageExtendedElements,
  selectPdfFormTemplateDependenceGroupsById,
} from 'store/forms/selectors';
import { selectIsPopupActive } from 'store/global/selectors';

import { useDeleteMultipleOptions } from './hooks/useDeleteMultipleOptions';
import PdfCardElement from './PdfCardElement';

const PdfCardWithElements = ({ selectedPage, pdf, isEditFormPath, scale }) => {
  const dispatch = useDispatch();

  const dynamicFieldsSchema = useSelector(selectDynamicFieldsSchema);
  const dynamicFieldsMap = useSelector(selectDynamicFieldsMap);
  const dynamicFieldsNamesById = useSelector(selectDynamicFieldsNamesById);
  const pageElements = useSelector(selectPdfFormPageExtendedElements(selectedPage, scale));
  const pageElementsWithoutScaling = useSelector(
    selectPdfFormPageExtendedElements(selectedPage, 1),
  );
  const initialDependenceGroups = useSelector(selectPdfFormTemplateDependenceGroupsById);
  const isPopupActive = useSelector(selectIsPopupActive);
  const [args, setArgs] = useState(null);

  useDeleteMultipleOptions(args, selectedPage, setArgs);

  const editor = useMemo(
    () => ({
      formType: FormType.PDF,
      config: {
        get: () => ({
          dynamicFieldsSchema,
          dynamicFieldsNamesById,
        }),
      },
      commands: {
        get: operation => ({
          execute: args => {
            switch (operation) {
              case FormBuilderOperationType.UNDO:
                dispatch(deleteTemplateElementAction({ elementIndex: args['data-index'] }));
                break;

              default:
                break;
            }
          },
        }),
      },
      execute: (method, args) => {
        switch (method) {
          case FormBuilderMethodType.DELETE_REACT_COMPONENT:
            if (args) {
              dispatch(deleteTemplateElementAction({ elementIndex: args['data-index'] }));

              break;
            }

            break;

          case FormBuilderMethodType.INSERT_REACT_COMPONENT: {
            const getUpdatedProps = () => {
              switch (args.dataTemplateFieldType) {
                case ReactRenderingContent.SIGNATURE: {
                  const baseProps = {
                    tag: args.dataHellosignTag,
                    isRequired: args.dataHellosignRequired,
                    signer: args.dataHellosignSigner,
                    value: args.dataHellosignSigner,
                    usePoaVerbiage: args.dataSigningUsePoaVerbiage,
                    displaySignatureLine: args.dataSigningDisplaySignatureLine,
                  };

                  return baseProps;
                }

                case ReactRenderingContent.DYNAMIC_FIELD:
                  return {
                    value: args.dataDynamicValue,
                    type: args.dataDynamicType,
                    title: args.dataDynamicTitle || dynamicFieldsNamesById[args.dataDynamicValue],
                    label: args.dataDynamicLabel,
                    withLabel: args.dataDynamicWithLabel,
                    usePoaVerbiage: args.dataDynamicUsePoaVerbiage,
                    action: args.dataDynamicAction,
                    style: args.dataDynamicStyle,
                    postfix: args.dataDynamicPostfix,
                    prefix: args.dataDynamicPrefix,
                    multiplier: args.dataDynamicMultiplier,
                    width: args.width,
                    height: args.height,
                  };

                default:
                  return undefined;
              }
            };

            const updatedProps = getUpdatedProps();

            const actionBody = {
              updatedField: args['data-element'],
              elementIndex: args['data-index'],
              updatedProps,
            };

            dispatch(updateTemplateElementAction(actionBody));

            break;
          }

          case FormBuilderMethodType.INSERT_MULTIPLE_OPTIONS: {
            const getActionBody = () => {
              switch (args.dataTemplateFieldType) {
                case ReactRenderingContent.SIGNATURE: {
                  const { rootDependenceGroupId, isRemoved, dependenceGroups, elements, ...rest } =
                    args;

                  const dependenceGroupById = keyBy(dependenceGroups, 'id');

                  const updatedDependenceGroups = isRemoved
                    ? _.omit(
                        _.pickBy(
                          initialDependenceGroups,
                          item => item.rootDependenceGroupId !== rootDependenceGroupId,
                        ),
                        rootDependenceGroupId,
                      )
                    : Object.assign(initialDependenceGroups, dependenceGroupById);

                  const filteredElements = pageElementsWithoutScaling.filter(
                    el => el.helloSignTag?.rootDependenceGroupId !== rootDependenceGroupId,
                  );

                  const updatedElements = [...filteredElements, ...elements];

                  return {
                    ...rest,
                    elements: updatedElements,
                    dependenceGroups: Object.values(updatedDependenceGroups),
                  };
                }

                default:
                  return undefined;
              }
            };

            const actionBody = getActionBody();

            dispatch(updateTemplateElementMultipleOptionsAction(actionBody));

            break;
          }

          case FormBuilderMethodType.DELETE_MULTIPLE_OPTIONS: {
            switch (args.dataTemplateFieldType) {
              case ReactRenderingContent.SIGNATURE: {
                setArgs(args);
                break;
              }

              default:
                break;
            }

            break;
          }

          default:
            break;
        }
      },
    }),
    [
      dynamicFieldsSchema,
      dynamicFieldsNamesById,
      dispatch,
      initialDependenceGroups,
      pageElementsWithoutScaling,
    ],
  );

  const onDragStop = useCallback(
    (__, { x, y }, elementIndex) => {
      dispatch(
        updateTemplateElementAction({
          updatedField: 'position',
          updatedProps: { x: x / scale, y: y / scale },
          elementIndex,
        }),
      );
    },
    [dispatch, scale],
  );

  const onResizeStop = useCallback(
    (...args) => {
      const { style, dataset } = args[2];
      const { x, y } = args[4];

      dispatch(
        updateTemplateElementAction({
          updatedField: 'position',
          elementIndex: parseInt(dataset.index),
          updatedProps: {
            x: x / scale,
            y: y / scale,
            width: parseFloat(style.width) / scale,
            height: parseFloat(style.height) / scale,
          },
        }),
      );
    },
    [dispatch, scale],
  );

  return (
    <div className="relative">
      <PdfCard num={selectedPage} pdf={pdf} scale={scale} />

      {pageElements.map(({ position, index, helloSignTag, field }) => {
        const isNewItem = !!position?.isNewItem;
        const isExistsInSchema = !!dynamicFieldsNamesById[field?.value];
        const isLabelAlwaysVisible = dynamicFieldsMap[field?.value]?.isLabelAlwaysVisible;
        const isTagName = checkIsTagName(field?.title);
        const isTagAssignedDate = checkIsTagAssignedDate(field?.title);
        const isTag = isTagName || isTagAssignedDate;

        const isEmptyDynamicField = !isTag && !isNewItem && !!(field && !isExistsInSchema);
        const isDisabledDragging = !isEditFormPath || isPopupActive || isEmptyDynamicField;
        const isEnabledResizing =
          isEditFormPath && !isPopupActive && !!(helloSignTag || field?.title);

        return (
          <PdfCardElement
            editor={editor}
            field={field}
            helloSignTag={helloSignTag}
            index={index}
            isDisabledDragging={isDisabledDragging}
            isEditFormPath={isEditFormPath}
            isEmptyDynamicField={isEmptyDynamicField}
            isEnabledResizing={isEnabledResizing}
            isExistsInSchema={isExistsInSchema}
            isLabelAlwaysVisible={isLabelAlwaysVisible}
            key={index}
            position={position}
            scale={scale}
            selectedPage={selectedPage}
            onDragStop={onDragStop}
            onResizeStop={onResizeStop}
          />
        );
      })}
    </div>
  );
};

PdfCardWithElements.propTypes = {
  selectedPage: PropTypes.number.isRequired,
  scale: PropTypes.number.isRequired,
  isEditFormPath: PropTypes.bool.isRequired,
  pdf: PropTypes.object,
};

PdfCardWithElements.displayName = 'PdfCardWithElements';

export default PdfCardWithElements;
